/**
 * ForgotPasswordPage (Doc 54) — public route #/forgot-password.
 *
 * Anti-enumeration: backend luôn trả 200 → frontend hiển thị message
 * "Nếu email tồn tại, link reset đã được tạo. Liên hệ admin nhận URL."
 *
 * v1.1 sẽ tích hợp SMTP gửi email tự động.
 */
import { ArrowLeft, Mail, Send } from "lucide-react";
import * as React from "react";

import { authApi } from "@/api/auth";
import { ApiError } from "@/api/client";
import { Button } from "@/components/ui/button";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { BrandLogo, GradientText } from "@/components/ui/gradient";
import { Input } from "@/components/ui/input";

export function ForgotPasswordPage(): JSX.Element {
  const [email, setEmail] = React.useState("");
  const [submitting, setSubmitting] = React.useState(false);
  const [submitted, setSubmitted] = React.useState(false);
  const [error, setError] = React.useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent): Promise<void> => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);
    try {
      await authApi.forgotPassword(email.trim().toLowerCase());
      setSubmitted(true);
    } catch (e) {
      const msg =
        e instanceof ApiError
          ? e.status === 429
            ? "Quá nhiều yêu cầu. Vui lòng thử lại sau 1 giờ."
            : e.message
          : e instanceof Error
            ? e.message
            : String(e);
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-mesh p-4 relative overflow-hidden">
      <div
        aria-hidden
        className="absolute -top-40 -left-40 size-96 rounded-full bg-gradient-brand opacity-20 blur-3xl"
      />
      <Card className="w-full max-w-md relative z-10 shadow-card-lg backdrop-blur-xl bg-background/95">
        <CardHeader className="text-center pb-4">
          <div className="flex justify-center mb-3">
            <BrandLogo size={64} />
          </div>
          <CardTitle className="text-xl">
            <GradientText as="span" className="font-bold">
              Quên mật khẩu
            </GradientText>
          </CardTitle>
          <CardDescription>
            Nhập email để yêu cầu đặt lại mật khẩu
          </CardDescription>
        </CardHeader>

        {submitted ? (
          <>
            <CardContent className="space-y-3">
              <div className="rounded-md bg-primary/10 border border-primary/30 p-3 text-sm">
                <p className="font-medium">Yêu cầu đã được gửi</p>
                <p className="text-muted-foreground mt-1">
                  Nếu email tồn tại trong hệ thống, link đặt lại mật khẩu đã
                  được tạo. Trong v1, vui lòng <strong>liên hệ admin</strong>{" "}
                  để nhận URL setup (chưa có email tự động).
                </p>
              </div>
              <p className="text-xs text-muted-foreground">
                Admin có thể xem yêu cầu trong{" "}
                <code>Admin → Audit logs</code> hoặc tạo trực tiếp reset URL
                trong <code>Admin → Users</code>.
              </p>
            </CardContent>
            <CardFooter>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => {
                  window.location.hash = "/";
                }}
              >
                <ArrowLeft className="size-4 mr-1.5" /> Về trang đăng nhập
              </Button>
            </CardFooter>
          </>
        ) : (
          <form onSubmit={(e) => void handleSubmit(e)}>
            <CardContent className="space-y-3">
              <div className="space-y-1.5">
                <label htmlFor="email" className="text-sm font-medium">
                  Email
                </label>
                <div className="relative">
                  <Mail className="absolute left-3 top-1/2 -translate-y-1/2 size-4 text-muted-foreground" />
                  <Input
                    id="email"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="pl-9"
                    autoComplete="email"
                    required
                    autoFocus
                  />
                </div>
              </div>
              {error ? (
                <div className="rounded-md bg-destructive/10 border border-destructive/30 p-2.5 text-sm text-destructive">
                  {error}
                </div>
              ) : null}
            </CardContent>
            <CardFooter className="flex-col gap-2">
              <Button
                type="submit"
                variant="brand"
                className="w-full h-10"
                disabled={submitting || email.length < 3}
              >
                {submitting ? (
                  "Đang gửi..."
                ) : (
                  <>
                    <Send className="size-4 mr-1.5" /> Gửi yêu cầu
                  </>
                )}
              </Button>
              <button
                type="button"
                className="text-xs text-muted-foreground hover:underline"
                onClick={() => {
                  window.location.hash = "/";
                }}
              >
                Về trang đăng nhập
              </button>
            </CardFooter>
          </form>
        )}
      </Card>
    </div>
  );
}
